import { Button } from "components/DesignSystem/Button/Button";
import { FileInput, FileType } from "components/FileInput/FileInput";
import { DeleteIcon } from "components/icons/delete";
import { SmallWarningInfo } from "components/icons/SmallWarningInfo";
import { useToast } from "hooks/useToast";
import { useParseCOIMutation } from "store/apis/onboarding";
import { EntityBoxHeader } from "../EntityBoxHeader";

type UploadedModeProps = {
  disableDelete: boolean;
  onDelete: () => void;
  coiFile: File | null;
  onDrop: (files: FileType[]) => void;
  onDeleteCoi: () => void;
  onFileClick: () => void;
  onCancel: () => void;
  setParsedCOIData: (val: {
    entityName: string;
    country: string;
    state: string;
    formationType: string;
    dateOfIncorporation: string | null;
    registeredAgent: string | null;
  }) => void;
  setEntryMode: (val: "manual") => void;
};

export const UploadedMode = ({
  disableDelete,
  onDelete,
  coiFile,
  onDrop,
  onDeleteCoi,
  onFileClick,
  onCancel,
  setParsedCOIData,
  setEntryMode,
}: UploadedModeProps) => {
  const [parseCOI, { isLoading: isParsing, isError }] = useParseCOIMutation();
  const {
    alertToast,
    successToast,
    loading: loadingToast,
    dismiss: dismissToast,
  } = useToast();

  const handleParseDocument = async () => {
    if (coiFile) {
      const loadingtoastId = loadingToast({
        title: "Please wait",
        message: "Parsing document...",
      });
      try {
        const parsedData = await parseCOI({ file: coiFile }).unwrap();

        const {
          entity_name,
          country_uuid,
          state_uuid,
          entity_type_uuid,
          date_of_incorporation,
          registered_agent,
        } = parsedData;

        setParsedCOIData({
          entityName: entity_name || "",
          country: country_uuid || "",
          state: state_uuid || "",
          formationType: entity_type_uuid || "",
          dateOfIncorporation: date_of_incorporation || null,
          registeredAgent: registered_agent || null,
        });

        setEntryMode("manual");
        successToast({ message: "Document parsed successfully" });
      } catch (error) {
        alertToast({
          title: "Parsing failed",
          message: "Reupload the file or enter details manually",
        });
      } finally {
        dismissToast(loadingtoastId);
      }
    }
  };

  return (
    <div className="t-border t-border-solid t-border-neutral-10 t-rounded-lg t-p-6">
      <div className="t-flex t-items-center t-justify-between t-mb-4">
        <EntityBoxHeader />
        {!disableDelete && (
          <Button
            type="button"
            customType="ghost"
            onClick={onDelete}
            size="small"
          >
            <DeleteIcon /> <span className="t-ml-1">Delete</span>
          </Button>
        )}
      </div>

      <div className="t-mb-5">
        <FileInput
          label="Certificate of Incorporation"
          withForm={false}
          file={coiFile}
          onDrop={onDrop}
          onDelete={onDeleteCoi}
          accept={{
            "application/pdf": [".pdf"],
          }}
          onFileClick={onFileClick}
          hasError={isError}
          rightComponent={
            isError && <SmallWarningInfo className="t-text-red t-size-4" />
          }
        />
      </div>

      <div className="t-flex t-justify-end t-gap-2">
        <Button type="button" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleParseDocument}
          customType="primary"
          isLoading={isParsing}
          disabled={isParsing}
        >
          Parse document
        </Button>
      </div>
    </div>
  );
};
